import { useCallback, useRef, useState } from "react"

import { toast } from "sonner"

import type { ChatAppRequest } from "@/api/models"

import { explorerChatWebSocketApi, scholarChatWebSocketApi } from "@/api/api"
import { safeImport } from "@/lib/chunk-reload"
import { SettingsService } from "@/services/settings-service"

export type StreamingChatMessage = {
  id: string
  role: "user" | "assistant"
  content: string
  created_at: number
}

type UseStreamingChatOptions = {
  useAgentMode: boolean
  buildRequest: (history: ChatAppRequest["messages"]) => ChatAppRequest
  files: File[]
  onResponseComplete?: (content: string) => void
}

export function useStreamingChat({
  useAgentMode,
  buildRequest,
  files,
  onResponseComplete,
}: UseStreamingChatOptions) {
  const [messages, setMessages] = useState<StreamingChatMessage[]>([])
  const [isStreaming, setIsStreaming] = useState(false)
  const [streamingContent, setStreamingContent] = useState("")
  const [error, setError] = useState<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  const addMessage = useCallback((message: StreamingChatMessage) => {
    setMessages((prev) => [...prev, message])
  }, [])

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim()
      if (!trimmed || isStreaming) return

      setError(null)

      const userMessage: StreamingChatMessage = {
        id: crypto.randomUUID(),
        role: "user",
        content: trimmed,
        created_at: Date.now(),
      }
      addMessage(userMessage)

      const assistantMessageId = crypto.randomUUID()
      setIsStreaming(true)
      setStreamingContent("")

      const abortController = new AbortController()
      abortControllerRef.current = abortController

      try {
        const settingsService = SettingsService.getInstance()
        const token = settingsService.token

        const historyForModel: ChatAppRequest["messages"] = [
          ...messages.map((m) => ({
            role: m.role,
            content: m.content,
          })),
          { role: "user", content: trimmed },
        ]

        const request = buildRequest(historyForModel)

        const response = useAgentMode
          ? await scholarChatWebSocketApi(
              request,
              files.length > 0 ? files : undefined,
              token,
              abortController.signal,
              () => {}
            )
          : await explorerChatWebSocketApi(
              request,
              files.length > 0 ? files : undefined,
              token,
              abortController.signal,
              () => {},
              "open",
              null
            )

        if (!response.body) {
          throw new Error("No response body")
        }

        const { default: readNDJSONStream } = await safeImport(
          () => import("ndjson-readablestream")
        )
        let accumulated = ""

        for await (const event of readNDJSONStream(response.body)) {
          if (!event) continue
          if (abortController.signal.aborted) {
            const abortError = new Error("AbortError")
            abortError.name = "AbortError"
            throw abortError
          }

          if (event?.error || event?.type === "error") {
            const errorMessage = String(event?.message || event?.error || "Unknown error")
            throw new Error(errorMessage)
          }

          if (typeof event.content === "string" && event.content.length > 0) {
            accumulated += event.content
            setStreamingContent(accumulated)
          }
        }

        const assistantMessage: StreamingChatMessage = {
          id: assistantMessageId,
          role: "assistant",
          content: accumulated,
          created_at: Date.now(),
        }
        addMessage(assistantMessage)
        setStreamingContent("")

        onResponseComplete?.(accumulated)
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err)
        if (message !== "AbortError" && err instanceof Error && err.name !== "AbortError") {
          setError(message)
          toast.error(message)
        }
      } finally {
        setIsStreaming(false)
        setStreamingContent("")
        abortControllerRef.current = null
      }
    },
    [isStreaming, addMessage, messages, files, buildRequest, useAgentMode, onResponseComplete]
  )

  const cancelStream = useCallback(() => {
    abortControllerRef.current?.abort()
    abortControllerRef.current = null
  }, [])

  return {
    messages,
    isStreaming,
    streamingContent,
    error,
    sendMessage,
    cancelStream,
  }
}
